import Vue from 'vue';
import filter from "lodash/filter";
import findIndex from "lodash/findIndex";
import find from "lodash/find";
import each from "lodash/each";
import countBy from "lodash/countBy";
// Cherry-picked lodash methods kept under `_` so existing call sites are unchanged
const _ = { filter, findIndex, find, each, countBy };
import {$http} from '@/utils/https';
import type {ActionContext} from "vuex";
import type {RootState} from "@/store";
import type {BasketProduct, Product, ProductPrice} from "@/types/shop";
import type { OrderProduct } from "@/types/order";
import { makeid } from "@/helpers/helpers";

const saveBasketProducts = (products: Array<BasketProduct>): void => {
  const parsed = JSON.stringify(products);
  localStorage.setItem('products', parsed);
};

const loadBasketProducts = (): Array<BasketProduct> => {
  if (localStorage.getItem('products')) {
    const products = JSON.parse(localStorage.getItem('products') || '[]');

    return _.filter(products);
  }

  return [];
}

// state
interface State {
  buyLoading: boolean;
  promoCode: string | null;
  discount: any;
  loadingPromoCode: boolean;
  productsWithDefaultPrices: Array<Product> | null;
  basket: Array<BasketProduct>;
  defaultBasket: Array<BasketProduct>;
  isShowBasketPopup: boolean;
  buyTogetherProducts: Array<Product>;
  clientProducts: Array<Product>;
  selectedBonuses: selectedBonuses,
  promotionResponse: any
}

interface selectedBonuses {
  pay_bonus_amount: number
}

interface AddBasketPayload {
  select_count: number;
  product_price: ProductPrice;
  product: Product;
  discount?: number;
  isPresent: any;
  isAddedFromSale: any;
  isFree?: boolean;
  saleId?: number | null;
  pricePer1: number;
  discountPer1: number;
  allProductSum: number;
}

interface SetCountPayload {
  product: BasketProduct;
  select_count: number;
}

interface HandlePayload {
  basketProduct: BasketProduct;
  product: Product;
  productPrice: ProductPrice;
}

interface FindProductData {
  basketProduct: BasketProduct;
  product: Product;
  productPrice: ProductPrice;
}


const state: State = {
  buyLoading: false,
  promoCode: '',
  discount: null,
  loadingPromoCode: false,
  productsWithDefaultPrices: null,
  basket: loadBasketProducts(),
  defaultBasket: [],
  isShowBasketPopup: false,
  buyTogetherProducts: [],
  clientProducts: [],
  promotionResponse: null,
  selectedBonuses: {
    pay_bonus_amount: 0
  }
};

const hasProductTypeInBasket = (list: Array<BasketProduct>, type: string): boolean => {
  const counts = _.countBy(list, 'type');

  return (counts[type] || 0) > 0
}

// getters
const getters = {
  selectedBonuses: (state: State) => state.selectedBonuses,
  buyTogetherProducts: (state: State) => state.buyTogetherProducts,
  isShowBasketPopup: (state: State) => state.isShowBasketPopup,
  productsWithDefaultPrices: (state: State) => state.productsWithDefaultPrices,


  promoCode: (state: State) => state.promoCode,
  discount: (state: State) => state.discount,
  buyLoading: (state: State) => state.buyLoading,
  loadingPromoCode: (state: State) => state.loadingPromoCode,
  basket: (state: State) => state.basket || [],
  clientProducts: (state: State) => state.clientProducts || [],
  promotionResponse: (state: State) => state.promotionResponse,
};


// actions
const actions = {
  // CHECK_PROMO_CODE: async ({commit}: ActionContext<State, RootState) => {
  //   commit('CHECK_PROMO_CODE_LOADING', true);
  //   commit('RESET_DISCOUNT');
  //   try {
  //     const response = await $http.post(`v1/order/coupon`, {code: state.promoCode});
  //
  //     commit('SET_DISCOUNT', response.data.data.percent);
  //     return response.data;
  //   } catch (e) {
  //     throw e;
  //   } finally {
  //     commit('CHECK_PROMO_CODE_LOADING', false);
  //   }
  // },

  GET_BUY_TOGETHER_PRODUCT: async ({commit}: ActionContext<State, RootState>, payload: {
    ids: Array<number | string>
  }) => {
    // commit('CHECK_PROMO_CODE_LOADING', true);
    try {
      const response = await $http.get('v1/basket/products/buy-with-in-basket', {
        params: {
          products: payload.ids,
        }
      });
      commit('SET_BUY_TOGETHER_PRODUCT', response.data.data);
      // return response.data;
    } catch (e) {
      throw e;
    } finally {
      // commit('CHECK_PROMO_CODE_LOADING', false);
    }
  },
  GET_SYNC_BASKET: async ({commit}: ActionContext<State, RootState>, payload: { ids: Array<number | string> }) => {
    commit('CHECK_PROMO_CODE_LOADING', true);
    try {
      const response = await $http.get('v1/basket/products', {
        params: {
          ids: payload.ids
        }
      });
      // commit('SET_BASKET_PRODUCTS', response.data.data);
      return response.data;
    } catch (e) {
      throw e;
    } finally {
      commit('CHECK_PROMO_CODE_LOADING', false);
    }
  },
  GET_DEFAULT_PRICE_BASKET: async ({commit}: ActionContext<State, RootState>, payload: {
    ids: Array<number | string>
  }) => {
    commit('CHECK_PROMO_CODE_LOADING', true);
    try {
      const response = await $http.get('v1/basket/products', {
        params: {
          ids: payload.ids,
          default_price: 1
        }
      });
      commit('SET_PRODUCTS_WITH_DEFAULT_PRICES', response.data.data);
      return response.data;
    } catch (e) {
      console.error(e)
    } finally {
      commit('CHECK_PROMO_CODE_LOADING', false);
    }
  },
  FETCH_PROMOCODE_INFO: async ({commit}: ActionContext<State, RootState>, payload: any) => {
    try {
      type TFetchResponse = { products: Array<any>, promocode: { value: string | null, activated: boolean } };

      const response = await $http.post<TFetchResponse>('/v1/checkout/sync-basket', payload);
      const mergedProducts: { [key: string]: BasketProduct } = {};

      // @ts-ignore
      response.data.order.orderProducts.data.forEach((orderData: OrderProduct) => {
        const foundProduct = response.data.products.find((product: any) => product.productPrice.id === orderData.productPriceId);
        if (!foundProduct) return;

        const productKey = `${foundProduct.product.id}-${foundProduct.productPrice.id}`;

        if (mergedProducts[productKey]) {
          mergedProducts[productKey].select_count += orderData.count;
          // @ts-ignore
          mergedProducts[productKey].discount += orderData.discount || 0;
          // @ts-ignore
          mergedProducts[productKey].allProductSum += orderData.sum || 0;
        } else {
          // @ts-ignore
          const fpIsFree = foundProduct.isFree;
          // @ts-ignore
          const fpSaleId = foundProduct.saleId;
          // @ts-ignore
          const odIsFree = orderData.isFree;
          // @ts-ignore
          const odSaleId = orderData.saleId;
          mergedProducts[productKey] = makeBasketProduct({
            product: foundProduct.product,
            product_price: foundProduct.productPrice,
            select_count: orderData.count,
            discount: orderData.discount || 0,
            isPresent: orderData.isPresent,
            isAddedFromSale: orderData.isAddedFromSale,
            isFree: fpIsFree ?? odIsFree,
            saleId: fpSaleId ?? odSaleId ?? null,
            pricePer1: orderData.pricePer1,
            discountPer1: orderData.discountPer1,
            // @ts-ignore
            allProductSum: orderData.sum
          });
        }
      });

      // @ts-ignore
      state.basket = Object.values(mergedProducts);
      saveBasketProducts(state.basket);

      return response.data;
    } catch (e) {
      throw e
    }
  },
  FETCH_PROMOTION: async ({ commit }: ActionContext<State, RootState>, payload: any) => {
      console.log('FETCH_PROMOTION')
    try {
      type TFetchResponse = { products: Array<BasketProduct & { productPrice: ProductPrice, product: Product }>, promocode: { value: string | null, activated: boolean } };

      // Build a map of gift markers we know about: from what we just sent, and
      // from what is already persisted in state.basket. This survives any
      // backend response that forgets to echo is_free/sale_id, so gifts never
      // silently turn back into regular items after a sync.
      const giftMetaByPriceId: { [ppid: number]: { isFree: true; saleId: number | null } } = {};
      if (payload && Array.isArray(payload.products)) {
        payload.products.forEach((item: any) => {
          if (item && item.is_free) {
            giftMetaByPriceId[item.product_price_id] = {
              isFree: true,
              saleId: item.sale_id ?? null,
            };
          }
        });
      }
      state.basket.forEach((item: BasketProduct) => {
        // @ts-ignore
        const itemIsFree = item.isFree;
        // @ts-ignore
        const itemSaleId = item.saleId;
        if (itemIsFree && item.select_type?.id != null) {
          giftMetaByPriceId[item.select_type.id] = {
            isFree: true,
            saleId: itemSaleId ?? null,
          };
        }
      });

      const response = await $http.post<TFetchResponse>('/v1/checkout/sync-basket', payload);
      const mergedProducts: { [key: string]: BasketProduct } = {};

      commit('SET_PROMOTION_RESPONSE', response.data);

      const resolveGiftMeta = (ppid: number, foundProduct: any, orderData: any) => {
        const fromLocal = giftMetaByPriceId[ppid];
        const fpIsFree = foundProduct ? foundProduct.isFree : undefined;
        const odIsFree = orderData ? orderData.isFree : undefined;
        const localIsFree = fromLocal ? fromLocal.isFree : false;
        const isFree = !!(fpIsFree || odIsFree || localIsFree);
        const fpSaleId = foundProduct ? foundProduct.saleId : null;
        const odSaleId = orderData ? orderData.saleId : null;
        const localSaleId = fromLocal ? fromLocal.saleId : null;
        let saleId: number | null = null;
        if (fpSaleId != null) saleId = fpSaleId;
        else if (odSaleId != null) saleId = odSaleId;
        else if (localSaleId != null) saleId = localSaleId;
        return { isFree, saleId };
      };


      // Update existing basket products with pricing from response
      // @ts-ignore
      response.data.order.orderProducts.data.forEach((orderData: OrderProduct) => {
        const foundProduct = response.data.products.find(product => product.productPrice.id === orderData.productPriceId);

        if (foundProduct) {
          const productKey = `${foundProduct.product.id}-${foundProduct.productPrice.id}`;

          if (mergedProducts[productKey]) {
            mergedProducts[productKey].select_count += orderData.count;
            // @ts-ignore
            mergedProducts[productKey].discount += orderData.discount || 0;
            // @ts-ignore
            mergedProducts[productKey].allProductSum += orderData.sum || 0;
          } else {
            const giftMeta = resolveGiftMeta(foundProduct.productPrice.id, foundProduct, orderData);
            mergedProducts[productKey] = makeBasketProduct({
              product: foundProduct.product,
              product_price: foundProduct.productPrice,
              select_count: orderData.count,
              discount: orderData.discount || 0,
              isPresent: orderData.isPresent || giftMeta.isFree,
              isAddedFromSale: orderData.isAddedFromSale,
              isFree: giftMeta.isFree,
              saleId: giftMeta.saleId,
              pricePer1: orderData.pricePer1,
              discountPer1: orderData.discountPer1,
              // @ts-ignore
              allProductSum: orderData.sum
            });
          }
        } else {
          // Product not in response.products — update pricing on existing basket item
          const orderProductData = orderData.product?.data;
          if (!orderProductData) return;

          const basketItem = state.basket.find(
            (item: BasketProduct) => item.id === orderProductData.id
          );
          if (basketItem) {
            // @ts-ignore
            basketItem.discount = orderData.discount || 0;
            // @ts-ignore
            basketItem.pricePer1 = orderData.pricePer1;
            // @ts-ignore
            basketItem.discountPer1 = orderData.discountPer1;
            // @ts-ignore
            basketItem.allProductSum = orderData.sum;
            const giftMeta = resolveGiftMeta(basketItem.select_type.id, null, orderData);
            // @ts-ignore
            basketItem.isFree = giftMeta.isFree;
            // @ts-ignore
            basketItem.saleId = giftMeta.saleId;
            // Update currency and price from order data
            if (orderData.currency && basketItem.select_type) {
              basketItem.select_type.currency = orderData.currency;
              basketItem.select_type.price = orderData.pricePer1;
              basketItem.select_type.basePrice = orderData.pricePer1 + (orderData.discountPer1 || 0);
            }
            if (orderData.currency && basketItem.productPrices?.data?.[0]) {
              // @ts-ignore
              basketItem.productPrices.data[0].currency = orderData.currency;
            }
            mergedProducts[`${basketItem.id}-${basketItem.select_type.id}`] = basketItem;
          } else if (
            // @ts-ignore
            orderData.isPresent ||
            // @ts-ignore
            orderData.isAddedFromSale ||
            // @ts-ignore
            orderData.isFree ||
            giftMetaByPriceId[orderData.productPriceId]
          ) {
            // New gift item returned by backend — merge into basket from nested product data
            const nested: any = orderProductData;
            const nestedPriceList = nested.productPrices?.data || [];
            const nestedProductPrice =
              nestedPriceList.find((p: any) => p.id === orderData.productPriceId) ||
              nestedPriceList[0];
            if (nestedProductPrice) {
              const productForBuild: any = nested.product
                ? { ...nested.product }
                : { ...nested };
              if (!productForBuild.productPrices) {
                productForBuild.productPrices = nested.productPrices;
              }
              if (!productForBuild.image && nested.image) {
                productForBuild.image = nested.image;
              }
              if (!productForBuild.title && nested.title) {
                productForBuild.title = nested.title;
              }
              if (!productForBuild.slug && nested.slug) {
                productForBuild.slug = nested.slug;
              }
              const giftMeta = resolveGiftMeta(nestedProductPrice.id, null, orderData);
              // @ts-ignore
              const odIsPresent = orderData.isPresent;
              // @ts-ignore
              const odIsAddedFromSale = orderData.isAddedFromSale;
              mergedProducts[`${productForBuild.id}-${nestedProductPrice.id}`] =
                makeBasketProduct({
                  product: productForBuild,
                  product_price: nestedProductPrice,
                  select_count: orderData.count,
                  discount: orderData.discount || 0,
                  isPresent: odIsPresent || giftMeta.isFree,
                  isAddedFromSale: odIsAddedFromSale,
                  isFree: giftMeta.isFree,
                  saleId: giftMeta.saleId,
                  // @ts-ignore
                  pricePer1: orderData.pricePer1,
                  // @ts-ignore
                  discountPer1: orderData.discountPer1,
                  // @ts-ignore
                  allProductSum: orderData.sum,
                });
            }
          }
        }
      });

      // @ts-ignore
      state.basket = Object.values(mergedProducts);
      saveBasketProducts(state.basket);

      return response.data;
    } catch (e) {
        localStorage.setItem('products', '[]');
        state.basket = []
        console.error(e)
    }
  },
  GET_CLIENT_PRICE_BASKET: async ({commit}: ActionContext<State, RootState>, payload: {
    ids: Array<number | string>
  }) => {
    try {
      const response = await $http.get('v1/basket/products', {
        params: {
          ids: payload.ids,
          client_price: 1
        }
      });
      commit('SET_PRODUCTS_WITH_CLIENT_PRICES', response.data.data);
      return response.data;
    } catch (e) {
      throw e;
    } finally {
    }
  },
  GET_CLIENT_PRICE_BASKET_FOR_MODAL: async ({commit}: ActionContext<State, RootState>, payload: {
    ids: Array<number | string>
  }) => {
    try {
      const response = await $http.get('v1/basket/products', {
        params: {
          ids: payload.ids,
          client_price: 1
        }
      });
      commit('SET_PRODUCTS_WITH_CLIENT_PRICES_FOR_MODAL', response.data.data);
      return response.data;
    } catch (e) {
      throw e;
    } finally {
    }
  },
};


const reloadBasketProducts = (state: State) => {
  const newBasket = loadBasketProducts();

  if (newBasket.length !== state.basket.length) {
    state.basket = newBasket
  }
}

const findProductIndex = (list: Array<BasketProduct>, basketProduct: BasketProduct): number => {
  const foundHash =  _.findIndex(list, (product) => {
        return product.hash === basketProduct.hash
      },
  );
  if (foundHash) return foundHash
  return _.findIndex(list, ({id, select_type}) =>
      id === basketProduct.id && select_type.id === basketProduct.select_type.id
  );
};

const replaceProduct = (basketProduct: BasketProduct): Array<BasketProduct> => {
  const basketProducts = state.basket;

  const index = findProductIndex(basketProducts, basketProduct);
  if (index !== -1) {
    basketProducts[index] = basketProduct;
  } else {
    basketProducts.push(basketProduct)
  }

  return basketProducts;
}

const resolveProductCount = (basketProduct: BasketProduct, count: number | any): BasketProduct => {
  basketProduct.select_count = 0;

  if (!basketProduct.select_type.quantity) {
    basketProduct.select_count = count;
    return basketProduct;
  }

  const setQuantity = Math.min(
    Math.max(Number.parseFloat(count || '1'), 1),
    basketProduct.select_type.quantity
  );

  basketProduct.select_count = Math.ceil(setQuantity)

  return basketProduct;
}

const updateCountForProduct = (payload: SetCountPayload): Array<BasketProduct> => {
  const BasketProductsList = state.basket;

  console.log('change-count', payload.product.hash);

  const index = findProductIndex(BasketProductsList, payload.product);
  if (index !== -1) {
    BasketProductsList[index] = resolveProductCount(BasketProductsList[index], payload.select_count);
  }

  return BasketProductsList;
}

const makeBasketProduct = (payload: AddBasketPayload): BasketProduct => {
  const value = resolveProductCount({
    id: payload.product.id,
    title: payload.product.title,
    slug: payload.product.slug,
    image: payload.product.image,
    productPrices: payload.product.productPrices,
    type: payload.product.type,
    brand: payload.product.brand,
    brandSeries: payload.product.brandSeries,
    discount: payload.discount,

    select_count: payload.select_count || 1,
    select_type: payload.product_price,
    client_price: undefined,
    client_with_code_price: undefined,
    isPresent: payload.isPresent,
    isAddedFromSale : payload.isAddedFromSale,
    isFree: payload.isFree || false,
    saleId: payload.saleId ?? null,
    hash: makeid(12),
    // @ts-ignore
    pricePer1: payload.pricePer1,
    // @ts-ignore
    discountPer1: payload.discountPer1,
    // @ts-ignore
    allProductSum: payload.allProductSum
  }, payload.select_count)
  return value;
}

const updateBasketProduct = (basketProduct: BasketProduct, product: Product): BasketProduct => {
  // @ts-ignore
  return resolveProductCount({
    id: product.id,
    title: product.title,
    slug: product.slug,
    image: product.image,
    productPrices: product.productPrices,
    type: product.type,
    brand: product.brand,
    brandSeries: product.brandSeries,

    select_count: basketProduct.select_count,
    select_type: basketProduct.select_type,
    client_price: basketProduct.client_price,
    client_with_code_price: basketProduct.client_with_code_price,
    isPresent: basketProduct.isPresent,
    isAddedFromSale : basketProduct.isAddedFromSale,
    isFree: basketProduct.isFree || false,
    saleId: basketProduct.saleId ?? null,
    // @ts-ignore
    pricePer1: basketProduct.pricePer1,
    // @ts-ignore
    discountPer1: basketProduct.discountPer1,
    // @ts-ignore
    discount: basketProduct.discount,
    // @ts-ignore
    allProductSum: basketProduct.allProductSum
  }, basketProduct.select_count)
}

const findProduct = (products: Array<Product>, basketProduct: BasketProduct): FindProductData | null => {
  const product = _.find(products, ({id}) => basketProduct.id === id)

  if (!product) {
    return null;
  }

  const productPrice = _.find(product.productPrices.data, ({value}) => basketProduct.select_type.value === value)

  if (!productPrice) {
    return null;
  }

  return {basketProduct, product, productPrice}
}

const refreshBasketProducts = (basketProducts: Array<BasketProduct>, products: Array<Product>, itemHandler: (payload: HandlePayload) => BasketProduct) => {
  const basket: Array<BasketProduct> = [];

  _.each(basketProducts, (basketProduct: BasketProduct): void => {
    const productData = findProduct(products, basketProduct)

    if (!productData) {
      basket.push(basketProduct)
      return
    }

    basket.push(updateBasketProduct(itemHandler(productData), productData.product))
  });

  return basket;
};

const addProductToBasket = (state: State, payload: AddBasketPayload): Array<BasketProduct> => {
  if (payload.product.hasDuplicate) {
    return state.basket
  }

  let basketProduct = state.basket.find(basketProduct => basketProduct.id === payload.product.id
    && basketProduct.select_type.value === payload.product_price.value);

  if (basketProduct) {
    payload.select_count++;
  } else {
    basketProduct = makeBasketProduct(payload)
  }

  return replaceProduct(basketProduct);
}

setInterval(() => {
  reloadBasketProducts(state)
}, 10000);

// mutations
const mutations = {
  ADD_ITEM_TO_BASKET(state: State, payload: AddBasketPayload) {
    state.basket = addProductToBasket(state, payload)

    saveBasketProducts(state.basket);
  },
  ADD_WEBINAR_ITEM_TO_BASKET(state: State, payload: AddBasketPayload) {
    state.basket = addProductToBasket(state, payload)

    saveBasketProducts(state.basket);
  },
  REMOVE_ITEM_FROM_BASKET(state: State, basketProduct: BasketProduct) {
    const foundProduct = state.basket.find(product => product.id === basketProduct.id)
    if (foundProduct) {
      // @ts-ignore
      window.dataLayer.push({ ecommerce: null });
      // @ts-ignore
      window.dataLayer.push({
        event: "remove_from_cart",
        ecommerce: {
          // @ts-ignore
          currency: foundProduct.select_type.currency || "EUR",
          value: foundProduct.select_type.price,
          items: [
            {
              item_id: foundProduct.select_type.id,
              item_name: foundProduct.title,
              affiliation: "Renew-Cosmetics",
              coupon: "",
              discount:  0.00,
              index: 0,
              item_brand: foundProduct?.brand?.data?.title || 'Renew',
              item_category: foundProduct?.brandSeries?.data?.title || '',
              item_list_id: "Cosmetics",
              item_list_name: "Cosmetics",
              location_id: "ChIJIQBpAG2ahYAR_6128GcTUEo",
              price: foundProduct.select_type.price,
              quantity: 1
            }
          ]
        }
      });
    }
    Vue.delete(state.basket, findProductIndex(state.basket, basketProduct))
    saveBasketProducts(state.basket);
  },
  RESET_BASKET(state: State) {
    state.basket = [];

    saveBasketProducts(state.basket)
  },
  CHECK_PROMO_CODE_LOADING(state: State, status: boolean) {
    state.loadingPromoCode = status;
  },
  INCREMENT_BASKET_LIST_COUNT(state: State, basketProduct: BasketProduct) {
    state.basket = updateCountForProduct({
      select_count: basketProduct.select_count + 1,
      product: basketProduct
    })

    saveBasketProducts(state.basket);
  },
  SET_BASKET_LIST_COUNT(state: State, countPayload: SetCountPayload) {
    console.log(countPayload);
    state.basket = updateCountForProduct(countPayload)

    saveBasketProducts(state.basket);
  },
  DECREMENT_BASKET_LIST_COUNT(state: State, basketProduct: BasketProduct) {
    state.basket = updateCountForProduct({
      select_count: basketProduct.select_count - 1,
      product: basketProduct
    })

    saveBasketProducts(state.basket);
  },
  SET_PROMO_CODE_IN_BASKET(state: State, code: string | null) {
    state.promoCode = code;
  },
  SET_DISCOUNT(state: State, discount: any) {
    state.discount = discount;
  },
  RESET_DISCOUNT(state: State) {
    state.discount = null;
  },
  SET_BASKET_PRODUCTS(state: State, products: Array<Product>) {
    const basket: Array<BasketProduct> = []

    each(state.basket, (basketProduct: BasketProduct): void => {
      const productData = findProduct(products, basketProduct)

      if (!productData) return;

      // @ts-ignore
      basket.push(makeBasketProduct({
        product: productData.product,
        product_price: productData.productPrice,
        select_count: basketProduct.select_count || 1
      }))
    })

    state.basket = basket;
    saveBasketProducts(state.basket)
  },
  SET_PRODUCTS_WITH_DEFAULT_PRICES(state: State, products: Array<Product>) {
    state.productsWithDefaultPrices = products

    const itemHandler = ({basketProduct, productPrice}: HandlePayload): BasketProduct => {
      basketProduct.client_price = productPrice

      return basketProduct
    }

    state.basket = refreshBasketProducts(state.basket, products, itemHandler)
    saveBasketProducts(state.basket)
  },
  SET_PRODUCTS_WITH_CLIENT_PRICES(state: State, products: Array<Product>) {
    state.productsWithDefaultPrices = products

    const itemHandler = ({basketProduct, productPrice}: HandlePayload): BasketProduct => {
      basketProduct.client_with_code_price = productPrice

      return basketProduct
    }

    state.basket = refreshBasketProducts(state.basket, products, itemHandler)
    saveBasketProducts(state.basket)
  },
  SET_PRODUCTS_WITH_CLIENT_PRICES_FOR_MODAL(state: State, products: Array<Product>) {
    state.productsWithDefaultPrices = products
  },
  SET_BUY_TOGETHER_PRODUCT(state: State, data: Array<Product>) {
    state.buyTogetherProducts = data;
  },
  CLEAR_BYTOGETHER_PRODUCTS(state: State) {
    state.buyTogetherProducts = []
  },
  SET_USER_BONUSES(state: State, data: selectedBonuses) {
    state.selectedBonuses = data
  },
  SET_PROMOTION_RESPONSE(state: State, payload: any) {
    state.promotionResponse = payload
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
